import { memo } from "react";
import { Box, Card, Grid, Skeleton, Stack, useTheme } from "@mui/material";
import { marketplaceProductCardSx } from "../constants/marketplaceCardStyles";

const STORE_SKELETON_COUNT = 4;
const PRODUCT_SKELETON_COUNT = 8;

export const DispensersGridSkeleton = memo(function DispensersGridSkeleton() {
  return (
    <Grid container spacing={1.25}>
      {Array.from({ length: STORE_SKELETON_COUNT }).map((_, index) => (
        <Grid item xs={6} sm={4} md={3} key={index}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              minHeight: 128,
              p: 1.25,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Skeleton
              variant="rounded"
              width={72}
              height={22}
              sx={{ alignSelf: "flex-end", mb: 0.5 }}
            />
            <Skeleton variant="rounded" width={44} height={44} />
            <Skeleton variant="text" width="70%" height={20} />
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});

interface ProductGridSkeletonProps {
  compact?: boolean;
}

export const ProductGridSkeleton = memo(function ProductGridSkeleton({
  compact = false,
}: ProductGridSkeletonProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: compact
          ? {
              xs: "repeat(auto-fill, minmax(116px, 182px))",
              sm: "repeat(auto-fill, minmax(120px, 188px))",
              md: "repeat(auto-fill, minmax(124px, 196px))",
              lg: "repeat(auto-fill, minmax(128px, 204px))",
            }
          : {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
              lg: "repeat(5, minmax(0, 1fr))",
            },
        gap: compact ? 0.75 : 1.25,
      }}
    >
      {Array.from({ length: PRODUCT_SKELETON_COUNT }).map((_, index) => (
        <Stack
          key={index}
          sx={{
            width: "100%",
            overflow: "hidden",
            ...marketplaceProductCardSx(theme),
            "&:hover": { transform: "none", boxShadow: "none" },
          }}
        >
          <Skeleton
            variant="rectangular"
            sx={{ width: "100%", aspectRatio: "1 / 1" }}
          />
          <Stack spacing={0.75} sx={{ p: compact ? 1.15 : 1.25 }}>
            <Skeleton variant="text" width="90%" height={compact ? 38 : 40} />
            <Skeleton variant="text" width={72} height={24} />
            <Skeleton
              variant="rounded"
              height={compact ? 36 : 40}
              sx={{ mt: 0.5 }}
            />
          </Stack>
        </Stack>
      ))}
    </Box>
  );
});

/** Busca + grid de produtos enquanto a conta no dispensador carrega. */
export const StoreAccountSkeleton = memo(function StoreAccountSkeleton() {
  return (
    <Stack spacing={1.5} sx={{ width: "100%", minHeight: 280 }}>
      <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1.5 }} />
      <ProductGridSkeleton />
    </Stack>
  );
});
